import React from "react";
import { Link } from "react-router-dom";
import HUDFrame from "./HUDFrame";
import BudScanner from "./BudScanner";
import HowItWorks from "./HowItWorks";
import { CheckCircle2, ChevronsRight } from "lucide-react";

/**
 * Reusable industry / vertical-solution page template.
 * Matches the visual language of the existing feature pages so every
 * vertical solution reads as a first-class product.
 *
 * Props:
 *   pill:         short label above the hero title
 *   title:        main hero title (white)
 *   titleAccent:  hero title tail (green)
 *   tagline:      uppercase tagline
 *   description:  hero paragraph
 *   heroImage:    URL used inside the BudScanner reticle
 *   heroLabel:    e.g. "SCAN COMPLETE"
 *   heroScore:    e.g. "97.8%"
 *   sideStats:    [{ label, value, sub }]
 *   howSteps:     [{ icon, title, desc }]
 *   capabilities: [{ title, desc }]          — feature grid (usually 6)
 *   useCases:     [{ title, desc, kpi, sub }] — outcome cards (usually 3)
 *   metrics:      [{ label, value, sub }]    — big KPI strip (usually 4)
 *   quote:        { text, author, role }     — testimonial-style callout
 *   onRequestDemo: click handler
 */
export default function IndustryPage({
  pill,
  title,
  titleAccent,
  tagline,
  description,
  heroImage,
  heroLabel = "SCAN COMPLETE",
  heroScore = "97.8%",
  sideStats = [],
  howSteps = [],
  capabilities = [],
  useCases = [],
  metrics = [],
  quote,
  onRequestDemo,
}) {
  return (
    <div className="relative z-10" data-testid="industry-page">
      {/* HERO */}
      <section className="relative pt-28 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid gap-8 items-center lg:grid-cols-[1fr_1fr] xl:grid-cols-[1.05fr_1.1fr_260px]">
          <div>
            <div className="hud-pill reveal" data-testid="industry-pill">
              {pill}
            </div>
            <h1 className="mt-6 font-display font-black leading-[0.95] text-4xl sm:text-5xl lg:text-6xl reveal d1">
              <span className="text-white">{title}</span>
              <br />
              <span className="text-green glow">{titleAccent}</span>
            </h1>
            <div className="mt-4 text-lg md:text-xl font-display tracking-[0.08em] text-white/85 reveal d2">
              {tagline}
            </div>
            <p className="mt-5 max-w-xl text-[color:var(--ss-muted)] text-base md:text-lg leading-relaxed reveal d2">
              {description}
            </p>
            <div className="mt-8 flex flex-wrap gap-4 reveal d3">
              <button
                type="button"
                className="btn-primary"
                onClick={onRequestDemo}
                data-testid="industry-request-demo"
              >
                Request Demo
              </button>
              <Link to="/contact" className="btn-ghost">
                Talk to Sales
              </Link>
            </div>
          </div>

          <div className="relative reveal d2 pb-10">
            <BudScanner
              label={heroLabel}
              score={heroScore}
              image={heroImage}
              alt={title}
            />
          </div>

          {sideStats.length > 0 && (
            <div className="flex flex-col gap-4 reveal d3 lg:col-span-2 xl:col-span-1 lg:flex-row xl:flex-col lg:flex-wrap">
              {sideStats.map((s) => (
                <HUDFrame
                  key={s.label}
                  padding="p-4"
                  className="flex-1 xl:flex-none min-w-[180px]"
                >
                  <div className="text-[10px] font-display tracking-[0.28em] text-[color:var(--ss-green)]">
                    {s.label}
                  </div>
                  <div className="mt-1 text-2xl font-display font-black text-green glow">
                    {s.value}
                  </div>
                  {s.sub && (
                    <div className="text-xs text-[color:var(--ss-muted)] mt-1 tracking-wider">
                      {s.sub}
                    </div>
                  )}
                </HUDFrame>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      {howSteps.length > 0 && (
        <HowItWorks title="HOW IT WORKS" steps={howSteps} />
      )}

      {/* CAPABILITIES */}
      {capabilities.length > 0 && (
        <section className="py-14 relative" data-testid="industry-capabilities">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="hud-pill mb-4">CAPABILITIES</div>
            <h2
              className="font-display font-black mb-10"
              style={{ fontSize: "clamp(24px,3vw,40px)", letterSpacing: "0.04em" }}
            >
              <span className="text-white">EVERY INSPECTION.</span>{" "}
              <span className="text-green glow">FULLY AUTOMATED.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {capabilities.map((c) => (
                <HUDFrame key={c.title} padding="p-5">
                  <div className="text-[11px] tracking-[0.28em] font-display text-[color:var(--ss-green)]">
                    {c.title}
                  </div>
                  <p className="mt-3 text-[color:var(--ss-text)]/85 text-sm leading-relaxed">
                    {c.desc}
                  </p>
                </HUDFrame>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* METRICS STRIP */}
      {metrics.length > 0 && (
        <section className="py-10 relative" data-testid="industry-metrics">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <HUDFrame padding="p-6 md:p-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {metrics.map((m) => (
                  <div key={m.label} className="text-center">
                    <div className="text-[10px] tracking-[0.28em] font-display text-[color:var(--ss-green)]">
                      {m.label}
                    </div>
                    <div className="mt-2 font-display font-black text-green glow text-3xl md:text-4xl">
                      {m.value}
                    </div>
                    {m.sub && (
                      <div className="text-xs text-[color:var(--ss-muted)] mt-1 tracking-wider">
                        {m.sub}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </HUDFrame>
          </div>
        </section>
      )}

      {/* USE CASES */}
      {useCases.length > 0 && (
        <section className="py-14 relative" data-testid="industry-usecases">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="hud-pill mb-4">REAL-WORLD USE</div>
            <h2
              className="font-display font-black mb-10"
              style={{ fontSize: "clamp(24px,3vw,40px)", letterSpacing: "0.04em" }}
            >
              <span className="text-white">BUILT FOR</span>{" "}
              <span className="text-green glow">HIGH-STAKES OPERATIONS.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {useCases.map((u) => (
                <HUDFrame key={u.title} padding="p-6">
                  <div className="text-[10px] tracking-[0.26em] font-display text-[color:var(--ss-green)] flex items-center gap-2">
                    <span>OUTCOME</span>
                    {u.sub && (
                      <span className="px-1.5 py-0.5 rounded-sm border border-[color:var(--ss-green)]/50 text-[color:var(--ss-green)] text-[9px] tracking-[0.22em]">
                        {u.sub}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 font-display font-black text-green glow text-3xl">
                    {u.kpi}
                  </div>
                  <div className="mt-4 font-display font-bold text-white uppercase tracking-widest text-sm">
                    {u.title}
                  </div>
                  <p className="mt-2 text-[color:var(--ss-muted)] text-sm leading-relaxed">
                    {u.desc}
                  </p>
                </HUDFrame>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTE */}
      {quote && (
        <section className="py-12 relative">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <HUDFrame padding="p-8 md:p-10">
              <div
                className="font-display text-white"
                style={{ fontSize: "clamp(20px,2.4vw,30px)", lineHeight: 1.35 }}
              >
                <span className="text-[color:var(--ss-green)]">“</span>
                {quote.text}
                <span className="text-[color:var(--ss-green)]">”</span>
              </div>
              <div className="mt-5 text-xs tracking-[0.28em] font-display text-[color:var(--ss-green)]">
                — {quote.author} · <span className="text-[color:var(--ss-muted)]">{quote.role}</span>
              </div>
            </HUDFrame>
          </div>
        </section>
      )}

      {/* CTA / CROSS-PROMO */}
      <section className="py-16 relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-6">
          <HUDFrame>
            <div
              className="font-display font-black"
              style={{ fontSize: "clamp(22px,2.6vw,32px)" }}
            >
              <span className="text-white">DEPLOY IN WEEKS.</span>{" "}
              <span className="text-green glow">SCALE INDEFINITELY.</span>
            </div>
            <p className="mt-4 text-[color:var(--ss-muted)] leading-relaxed">
              Ship a working pilot on real product photos within 30 days.
              StrainScan Pro's platform is API-first, cloud-native, and
              trained continuously on new SKUs across your operation.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" className="btn-primary" onClick={onRequestDemo}>
                Request Demo
              </button>
              <Link to="/contact" className="btn-ghost inline-flex items-center gap-1">
                Custom Deployment <ChevronsRight size={18} />
              </Link>
            </div>
          </HUDFrame>
          <HUDFrame>
            <div className="text-xs tracking-[0.28em] font-display text-[color:var(--ss-green)]">
              WHY STRAINSCAN PRO
            </div>
            <ul className="mt-5 space-y-3 text-[color:var(--ss-text)]">
              {[
                "One platform — farm, warehouse, kitchen, retail",
                "API + on-device inference for offline deployments",
                "Enterprise security, SOC-friendly audit logs",
                "Continuous model retraining on your SKU catalog",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2
                    size={18}
                    className="text-[color:var(--ss-green)] mt-0.5 flex-shrink-0"
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </HUDFrame>
        </div>
      </section>
    </div>
  );
}
